import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { api, getProfileId } from './api';
import { VoiceInput } from './VoiceInput';
import { desktopAccountRequest } from './desktopAccountBridge';
import { markFieldPaste, fieldPasteRanges, type CorrectionRange } from './accountPasteAudit';

type Target = HTMLInputElement | HTMLTextAreaElement;
type Snapshot = {before: string; start: number; end: number; revision: string};
type Confirmation = {
  requestId: string; confirmedText: string; observedAt: string;
  destination: {kind: 'field' | 'draft'; field: string; startOffset: number; endOffset: number; revision: string};
  parentPasteIds?: string[]; parentInputPasteIds?: string[];
  correctionRanges?: CorrectionRange[];
};
type Retained = {snapshot: Snapshot; transcriptId: string; body: Confirmation; applied?: boolean};
const digest = async (text: string) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))].map(v => v.toString(16).padStart(2, '0')).join('');
function readRetained(key: string): Retained[] {
  const value = JSON.parse(localStorage.getItem(key) || 'null');
  return (Array.isArray(value) ? value : value ? [value] : []).filter(v =>
    v?.snapshot && typeof v.transcriptId === 'string' && v.body?.requestId);
}
const resultingText = (v: Retained) => v.snapshot.before.slice(0, v.snapshot.start) + v.body.confirmedText + v.snapshot.before.slice(v.snapshot.end);
export function clearSavedAccountVoice(userId: string, destination: string, savedText: unknown) {
  const key = `ig-voice:${userId}:${destination}`;
  try {
    const entries = readRetained(key), removed = new Set<string>();
    let text = savedText;
    for (let i = entries.length - 1; i >= 0; i--) {
      const item = entries[i];
      if (item.applied !== false && resultingText(item) === text) {
        removed.add(item.body.requestId); text = item.snapshot.before;
      }
    }
    const remaining = entries.filter(v => !removed.has(v.body.requestId));
    if (remaining.length) localStorage.setItem(key, JSON.stringify(remaining));
    else localStorage.removeItem(key);
  } catch { /* A completed save stays successful; retained speech is preserved. */ }
}

export function clearSavedProfileVoice(userId: string, values: Record<string, unknown>) {
  for (const field of ['name', 'studyGoal', 'citationStyleName']) {
    clearSavedAccountVoice(userId, `profile:${field}`, values[field]);
  }
}

/** Explicitly scoped profile prose. Never binds to whatever field gains focus. */
function NativeAccountVoiceField({userId, field, destination, inputId, onChange, disabled = false, native = false, nativeAvailable = true,
  sessionId, available = true, label = 'Dictate', savedText}: {
  userId: string; field?: 'name' | 'studyGoal' | 'citationStyleName'; destination?: string; inputId: string;
  onChange: (value: string) => void; disabled?: boolean; native?: boolean; nativeAvailable?: boolean;
  sessionId?: string; available?: boolean; label?: string; savedText?: string;
}) {
  const fieldId = destination ?? `profile:${field}`;
  const recoveryField = sessionId ? `session:${sessionId}:${fieldId}` : fieldId;
  const key = `ig-voice:${userId}:${recoveryField}`;
  const [retainedEntries, setRetainedEntries] = useState<Retained[]>(() => {
    if (native) return [];
    try {
      return readRetained(key);
    } catch { return []; }
  });
  const retained = retainedEntries.at(-1);
  const snapshot = useRef<Snapshot | undefined>(retained?.snapshot);
  const pending = useRef<Retained | undefined>(retained);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const owner = useRef(userId);
  owner.current = userId;
  const currentTarget = useRef({fieldId, sessionId, disabled, available});
  currentTarget.current = {fieldId, sessionId, disabled, available};
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (savedText !== undefined) {
      try {
        const before=readRetained(key);
        clearSavedAccountVoice(userId, recoveryField, savedText);
        const after=readRetained(key);
        if(after.length!==before.length) {
          setRetainedEntries(after);
          if(pending.current && !after.some(v=>v.body.requestId===pending.current!.body.requestId))pending.current=undefined;
        }
      } catch { /* Keep the displayed recoverable text if storage is unavailable. */ }
    }
  }, [userId, recoveryField, savedText]);
  function target(): Target {
    const element = document.getElementById(inputId);
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) ||
      element.disabled || element.readOnly || element.closest('[hidden]') || !element.isConnected)
      throw new Error('Return to this editable field before using voice.');
    return element;
  }
  async function capture() {
    const element = target();
    fieldPasteRanges(element); // Establish the pre-insertion undo/provenance snapshot.
    const before = element.value;
    const start = element.selectionStart ?? before.length;
    const end = element.selectionEnd ?? start;
    snapshot.current = {before, start, end, revision: await digest(before)};
    // Starting a new recording leaves earlier contributions in durable recovery.
    pending.current = undefined;
    return true;
  }
  function checked(phrase: string) {
    if (!alive.current || owner.current !== userId || (sessionId && getProfileId() !== userId) || currentTarget.current.fieldId !== fieldId || currentTarget.current.sessionId !== sessionId || currentTarget.current.disabled || !currentTarget.current.available) throw new Error('The destination changed or voice became unavailable. Your transcript has not been inserted.');
    const element = target(), saved = snapshot.current;
    if (!saved || element.value !== saved.before)
      throw new Error('This field changed while recording. The reviewed contribution is retained; no text was overwritten.');
    const text = saved.before.slice(0, saved.start) + phrase + saved.before.slice(saved.end);
    if (element.maxLength >= 0 && text.length > element.maxLength)
      throw new Error(`Shorten the transcript to fit the ${element.maxLength} character field.`);
    return {element, saved, text};
  }
  function insert(phrase: string, parents?: {parentPasteIds?: string[]; parentInputPasteIds?: string[]; correctionRanges?: CorrectionRange[]}) {
    const {element, saved, text} = checked(phrase);
    element.focus({preventScroll: true});
    element.setSelectionRange(saved.start, saved.end);
    if (!document.execCommand('insertText', false, phrase) || element.value !== text)
      throw new Error('The browser could not insert this transcript as an undoable edit. Your contribution is retained.');
    onChange(text);
    for (const range of parents?.correctionRanges || []) {
      for (const id of range.parentPasteIds) markFieldPaste(element,id,saved.start+range.startOffset,range.endOffset-range.startOffset,false);
      for (const id of range.parentInputPasteIds) markFieldPaste(element,id,saved.start+range.startOffset,range.endOffset-range.startOffset,true);
    }
    requestAnimationFrame(() => {
      if (alive.current && element.isConnected) {
        element.focus({preventScroll: true});
        element.setSelectionRange(saved.start + phrase.length, saved.start + phrase.length);
      }
    });
  }
  async function confirmAndInsert(text: string, id: string, parents?: {parentPasteIds: string[]; parentInputPasteIds: string[]; correctionRanges: CorrectionRange[]}) {
    const {saved} = checked(text);
    if (pending.current && !pending.current.body.correctionRanges &&
        ((pending.current.body.parentPasteIds?.length || 0)+(pending.current.body.parentInputPasteIds?.length || 0)>0))
      throw new Error('This older retained contribution has no exact correction ranges. Its text is kept for manual recovery; record a new contribution to insert with tracked corrections.');
    if (pending.current && (pending.current.transcriptId !== id || pending.current.body.confirmedText !== text))
      throw new Error('Retry the retained confirmation unchanged, then edit the field after insertion.');
    pending.current ??= {snapshot: saved, transcriptId: id, applied: false, body: {
      requestId: crypto.randomUUID(), confirmedText: text, observedAt: new Date().toISOString(),
      destination: {kind: sessionId && fieldId === 'draft' ? 'draft' : 'field', field: fieldId, startOffset: saved.start, endOffset: saved.end, revision: saved.revision},
      ...(parents?.parentPasteIds.length ? {parentPasteIds: parents.parentPasteIds} : {}),
      ...(parents?.parentInputPasteIds.length ? {parentInputPasteIds: parents.parentInputPasteIds} : {}),
      correctionRanges:parents?.correctionRanges || [],
    }};
    // Persist before confirmation; failures retain the exact original request.
    const entries = readRetained(key);
    if (!entries.some(v => v.body.requestId === pending.current!.body.requestId)) {
      if (entries.length >= 100) throw new Error('Save or recover the retained voice contributions before recording more.');
      entries.push(pending.current);
    }
    localStorage.setItem(key, JSON.stringify(entries));
    setRetainedEntries(entries);
    // A save acknowledgement can update recovery while this request awaits.
    // Keep the exact attempted contribution independent of the mutable UI ref.
    const contribution = pending.current;
    await api(`${sessionId ? `/sessions/${sessionId}` : ''}/transcriptions/${id}/confirm`, 'POST', contribution.body);
    insert(text, contribution.body);
    if (pending.current === contribution) pending.current = undefined;
    try {
      const appliedEntries = readRetained(key).map(entry =>
        entry.body.requestId === contribution.body.requestId ? {...entry, applied: true} : entry);
      localStorage.setItem(key, JSON.stringify(appliedEntries));
      setRetainedEntries(appliedEntries);
    } catch {
      setError('The transcript was inserted. Its device recovery copy could not be updated and has been kept.');
    }
  }
  async function use(text:string,id:string,parents?:{parentPasteIds:string[];parentInputPasteIds:string[];correctionRanges:CorrectionRange[]}) {
    setBusy(true);
    try {await confirmAndInsert(text,id,parents);}
    finally {if(alive.current)setBusy(false);}
  }
  return <div className="profile-voice-field">
    {native ? <button type="button" className="voice-button" disabled={disabled || busy || !nativeAvailable || !field || !!destination}
      title={nativeAvailable ? 'Dictate and review text' : 'Update Marvin to use voice in account settings'}
      onClick={async () => {
        if (busy || disabled) return;
        setBusy(true); setError('');
        try {
          await capture();
          const saved = snapshot.current!;
          const result = await desktopAccountRequest<{cancelled?: boolean; text?: string; parentPasteIds?: string[]; parentInputPasteIds?: string[]; correctionRanges?: CorrectionRange[]}>('dictateProfile', {
            field: field!, before: saved.before, start: `${saved.start}`, end: `${saved.end}`,
          });
          if (!result.cancelled && typeof result.text === 'string') insert(result.text, result);
        } catch (cause) { if (alive.current) setError((cause as Error).message); }
        finally { if (alive.current) setBusy(false); }
      }}><Mic size={17}/>{busy ? 'Reviewing voice…' : 'Dictate'}</button> :
      <VoiceInput key={key} sessionId={sessionId || ''} available={available} disabled={disabled} label={label}
        transcriptionPath={sessionId ? `/sessions/${sessionId}/transcriptions` : '/transcriptions'} onStart={capture} onUse={use}
        initialTranscript={retained ? {id: retained.transcriptId, text: retained.body.confirmedText,correctionRanges:retained.body.correctionRanges} : undefined}/>
    }
    {retainedEntries.length > 0 && <details><summary>Retained voice contributions</summary>
      <p>Reviewed text is kept until its field is saved. Changed fields are never overwritten automatically.</p>
      {retainedEntries.map(v => <p key={v.body.requestId}>{v.body.confirmedText}</p>)}
      <button type="button" disabled={busy} onClick={()=>{
        if (!window.confirm('Remove these reviewed voice copies from this device? Copy any words you still need first. Saved work and speech history are kept.')) return;
        try {
          const ids=new Set(retainedEntries.map(v=>v.body.requestId));
          const remaining=readRetained(key).filter(v=>!ids.has(v.body.requestId));
          if(remaining.length)localStorage.setItem(key,JSON.stringify(remaining));else localStorage.removeItem(key);
          if(pending.current && ids.has(pending.current.body.requestId))pending.current=undefined;
          setRetainedEntries(remaining);
        } catch {setError('Device storage could not remove these copies. They are still retained.');}
      }}>Remove reviewed device copies</button>
    </details>}
    {error && <p role="alert">{error}</p>}
  </div>;
}

// Browser account forms remain ordinary inputs. The hosted native account
// panel retains its on-device voice bridge.
export function AccountVoiceField(props: Parameters<typeof NativeAccountVoiceField>[0]) {
  return props.native ? <NativeAccountVoiceField {...props} /> : null;
}
